import { Component, computed, inject, signal, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { ThemePicker } from './theme-picker';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Subscription, filter } from 'rxjs';
import { AuthService } from '../services/auth';
import { ToastService } from '../services/toast';
import { StorageService } from '../services/storage';
import { MenuService } from '../services/menu';
import { PartnerKycService } from '../services/partner-kyc';
import { AdminNotificationService } from '../services/admin-notification';
import { IdleTimeoutService } from '../services/idle-timeout';
import { TestModeService } from '../services/test-mode';
import { ModeSwitch } from './mode-switch';
import { PageTitleService } from '../services/page-title';
import { AdminBell } from './admin-bell/admin-bell';
import { GlobalSearch } from './global-search/global-search';
import { refreshOnce } from '../interceptors/auth-interceptor';
import { jwtExpiryMs } from '../utils/jwt';

/** A sidebar node as returned by Menu/GetMyMenu (nested). */
interface MenuNode {
  menuId: number;
  parentId?: number | null;
  title: string;
  route?: string | null;
  icon?: string | null;
  permissionCode?: string | null;
  displayOrder?: number;
  isActive?: boolean;
  children?: MenuNode[];
}

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    ThemePicker,
    ModeSwitch,
    CommonModule,
    FormsModule,
    RouterOutlet,
    AdminBell,
    GlobalSearch,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
  ],
  templateUrl: './layout.html',
  styleUrls: ['./layout.css'],
})
export class Layout implements OnInit, OnDestroy {
  private auth = inject(AuthService);
  private router = inject(Router);
  private toast = inject(ToastService);
  private storage = inject(StorageService);
  private menuService = inject(MenuService);
  private kycService = inject(PartnerKycService);
  private notificationService = inject(AdminNotificationService);
  private idle = inject(IdleTimeoutService);
  private platformId = inject(PLATFORM_ID);
  /** Public so the template can show the TEST MODE bar. */
  readonly testMode = inject(TestModeService);

  /** Whether the logged-in user is an admin (controls the KYC alert bell). */
  isAdmin = false;

  loading = signal(false);
  menuLoading = signal(false);
  sidebarOpen = signal(false);
  userMenuOpen = signal(false);
  // Shell layout: 'vertical' = fixed left sidebar, 'horizontal' = top menu bar (desktop only).
  layoutMode = signal<'vertical' | 'horizontal'>('vertical');
  // Live clock shown centered in the topbar.
  clockTime = signal('');
  clockDate = signal('');
  private clockTimer: any;
  private routerSub?: Subscription;
  // Idle-session watchdog: checks the JWT's exp every 30s (and once on init).
  private sessionTimer: any;
  private refreshingSession = false;
  // Cross-tab logout sync: `storage` events fire in OTHER tabs when this origin's
  // localStorage changes, so a logout (or expiry cleanup) in one tab ends the session
  // everywhere immediately instead of leaving stale, dead shells behind.
  private storageListener = (e: StorageEvent) => {
    if ((e.key === 'token' || e.key === null) && !this.storage.getLocal('token')) {
      this.expireSession(true);
    }
  };

  search = signal('');
  currentUrl = signal('');
  expanded = signal<Set<number>>(new Set());
  // Derived straight from the shared service state, so the sidebar updates the moment the
  // menu loads — whether that's the authGuard (after login) or our own fetch below.
  menuTree = computed(() => this.menuService.myMenu() as MenuNode[]);
  user: any;

  // Recursively filter the tree by the search term (keep a parent if any child matches).
  filteredMenu = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.menuTree();
    const filterNodes = (nodes: MenuNode[]): MenuNode[] => {
      const out: MenuNode[] = [];
      for (const n of nodes) {
        const kids = n.children?.length ? filterNodes(n.children) : [];
        if (n.title.toLowerCase().includes(q) || kids.length) {
          out.push({ ...n, children: kids.length ? kids : n.children });
        }
      }
      return out;
    };
    return filterNodes(this.menuTree());
  });

  // Title shown in the topbar — the shared service derives it from the menu so the
  // top bar and every page heading always read the same menu name.
  readonly pageTitle = inject(PageTitleService).title;

  // Routes a non-approved partner may still use (mirrors the kycGuard's allow-list).
  private static readonly KYC_OPEN = new Set(['/partner-kyc', '/forbidden', '/change-password']);

  // The whole app is KYC-locked for a non-admin whose KYC isn't approved. Only flips on
  // once the status is actually known, so an approved partner's menu never flickers locked.
  readonly kycLocked = computed(
    () => !this.isAdmin && this.kycService.loaded() && !this.kycService.isApproved(),
  );

  get initial(): string {
    return (this.user?.username || 'U').charAt(0).toUpperCase();
  }

  constructor() {
    const storedUser = this.storage.getLocal('user');
    this.user = storedUser ? JSON.parse(storedUser) : null;
    this.isAdmin = this.auth.isAdmin();
  }

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    // Restore the chosen data view (live vs test records) and load the counts behind it.
    // Non-admins get 403 from the status call and simply never see the bar.
    this.testMode.initView();
    if (this.isAdmin) {
      // api/TestMode is Admin-role gated. Calling it as a partner 403s, and the error
      // interceptor toasts "Access denied" on EVERY page load — the service's own error
      // handler cannot suppress that, because the interceptor fires first.
      this.testMode.refresh();
    } else {
      // A partner has no switch: the server decides its view from its own mode, so ask what
      // that is — otherwise it reads test records with nothing on screen saying so.
      this.testMode.refreshPinned();
    }

    const mode = this.storage.getLocal('layoutMode');
    if (mode === 'horizontal' || mode === 'vertical') this.layoutMode.set(mode);

    this.tickClock();
    this.clockTimer = setInterval(() => this.tickClock(), 1000);

    this.currentUrl.set(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.currentUrl.set(e.urlAfterRedirects));

    this.loadMenu();

    // Session expiry is handled even while the user is IDLE (no request → the auth
    // interceptor never gets a 401 to react to): try one silent refresh, else auto-logout.
    this.checkSession();
    this.sessionTimer = setInterval(() => this.checkSession(), 30_000);
    window.addEventListener('storage', this.storageListener);
    // 15 minutes of user inactivity (cross-tab) ends the session — without this, the
    // watchdog above would silently keep refreshing an untouched session forever.
    this.idle.start();
  }

  ngOnDestroy() {
    if (this.clockTimer) clearInterval(this.clockTimer);
    if (this.sessionTimer) clearInterval(this.sessionTimer);
    // Every logout path unmounts this shell, so this is the single place the idle
    // tracker is torn down — no per-path cleanup to forget, no orphan listeners.
    this.idle.stop();
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('storage', this.storageListener);
    }
    // router.events outlives this component — without this, every logout/login cycle
    // leaks a subscription (and its closure) on the router's event stream.
    this.routerSub?.unsubscribe();
  }

  // ===================== idle-session watchdog =====================
  private checkSession() {
    const token = this.storage.getLocal('token');
    if (!token) {
      // The session ended elsewhere (another tab logged out, storage was cleared) but this
      // shell is still mounted — leave it silently instead of sitting on dead content.
      this.expireSession(true);
      return;
    }
    const exp = jwtExpiryMs(token);
    if (exp == null || Date.now() < exp - 10_000) return; // still valid (10s clock skew)

    if (this.refreshingSession) return;
    if (!this.storage.getLocal('refreshToken')) {
      this.expireSession();
      return;
    }
    // Shares the auth interceptor's refresh mutex, so a concurrent 401-triggered
    // refresh and this watchdog can never double-rotate the refresh token.
    this.refreshingSession = true;
    refreshOnce(this.auth).subscribe({
      next: () => (this.refreshingSession = false), // rotated silently — session stays alive
      error: () => {
        this.refreshingSession = false;
        this.expireSession();
      },
    });
  }

  /** The session is dead — clean up and land on login (once; the interceptors may
   *  already have done the toast/clear if the refresh call itself came back 401).
   *  `silent` = the session was already ended elsewhere (another tab / interceptor):
   *  just leave the shell without a second toast. */
  private expireSession(silent = false) {
    if (this.storage.getLocal('token')) {
      this.storage.clearAll();
      this.toast.error('Session expired. Please login again');
      this.router.navigate(['/login']);
    } else if (silent && !this.router.url.startsWith('/login')) {
      this.router.navigate(['/login']);
    }
    this.menuService.reset();
    this.kycService.reset();
    this.notificationService.reset();
  }

  private tickClock() {
    const d = new Date();
    this.clockTime.set(
      d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })
    );
    this.clockDate.set(
      d.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
    );
  }

  loadMenu() {
    // The authGuard usually loads GetMyMenu before this component activates; if so the
    // reactive menuTree() already has it. Otherwise fetch it now (also fills the guard's set).
    if (this.menuService.loaded()) {
      this.initExpanded();
      return;
    }
    this.menuLoading.set(true);
    this.menuService.loadMyMenu().subscribe({
      next: () => {
        this.menuLoading.set(false);
        this.initExpanded();
      },
      error: () => this.menuLoading.set(false),
    });
  }

  /** Start with every submenu COLLAPSED on first load; the user opens what they need (their
   *  toggles then persist for the session). Only the branch containing the CURRENT route is
   *  opened, so the active page stays visible in the sidebar. */
  private initExpanded() {
    const open = new Set<number>();
    const url = this.currentUrl();
    const walk = (nodes: MenuNode[], ancestors: number[]): boolean => {
      for (const n of nodes) {
        if (n.route && this.routeIsCurrent(n.route, url)) {
          ancestors.forEach((a) => open.add(a));   // open only this node's parent chain
          return true;
        }
        if (n.children?.length && walk(n.children, [...ancestors, n.menuId])) return true;
      }
      return false;
    };
    walk(this.menuTree(), []);
    this.expanded.set(open);
  }

  /** True when a menu node's route is (a prefix of) the current URL — reused by initExpanded. */
  private routeIsCurrent(route: string, url: string): boolean {
    const r = (route || '').trim();
    if (!r) return false;
    const path = url.split('?')[0].split('#')[0];
    return path === r || path.startsWith(r.endsWith('/') ? r : r + '/');
  }

  // ---- tree helpers ----
  isExpanded(id: number) {
    return this.expanded().has(id);
  }

  toggleExpand(id: number) {
    const set = new Set(this.expanded());
    set.has(id) ? set.delete(id) : set.add(id);
    this.expanded.set(set);
  }

  isActive(node: MenuNode): boolean {
    if (!node.route) return false;
    const r = node.route.trim();
    const route = r.startsWith('/') ? r : '/' + r;
    return this.currentUrl().split('?')[0] === route;
  }

  /** A leaf the non-approved partner can't open (everything but the KYC/auth pages). */
  isLocked(node: MenuNode): boolean {
    if (!this.kycLocked() || node.children?.length) return false;
    const r = (node.route || '').trim();
    if (!r) return false;
    const route = r.startsWith('/') ? r : '/' + r;
    return !Layout.KYC_OPEN.has(route);
  }

  iconOf(node: MenuNode): string {
    const icon = (node.icon || '').trim();
    if (!icon) return 'pi pi-circle';
    // Accept "pi pi-x", bare "pi-x", or a plain name "x".
    if (icon.startsWith('pi ')) return icon;
    if (icon.startsWith('pi-')) return `pi ${icon}`;
    return `pi pi-${icon}`;
  }

  /**
   * Horizontal mode only: place a top-level item's hover dropdown right under its
   * button. The menu row scrolls (so it clips), and the dropdown is position:fixed
   * to escape that clipping — which means it needs its viewport coords set here.
   * Flips leftward if it would run off the right edge.
   */
  onTopHover(e: MouseEvent) {
    if (this.layoutMode() !== 'horizontal' || !isPlatformBrowser(this.platformId)) return;
    const btn = e.currentTarget as HTMLElement;
    const node = btn.closest('.nav-node') as HTMLElement | null;
    if (!node || !node.classList.contains('depth-0')) return; // top-level items only
    const panel = node.querySelector(':scope > .nav-children') as HTMLElement | null;
    if (!panel) return;
    const r = btn.getBoundingClientRect();
    const width = panel.offsetWidth || 230;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    panel.style.left = `${left}px`;
    panel.style.top = `${r.bottom}px`;
  }

  onNode(node: MenuNode) {
    if (node.children?.length) {
      this.toggleExpand(node.menuId);
      return;
    }
    // KYC-locked partner: don't navigate — explain why with the same alert the guard uses.
    if (this.isLocked(node)) {
      const { title, text } = this.kycService.blockedAlert();
      this.toast.popup(text, title, 'warning');
      this.closeSidebar();
      return;
    }
    if (node.route) {
      this.router.navigate([node.route]);
      this.closeSidebar();
    } else {
      this.toast.info(`${node.title} is not linked to a page yet`);
    }
  }

  // ---- shell ----
  toggleSidebar() {
    this.sidebarOpen.update((v) => !v);
  }

  toggleLayoutMode() {
    const next = this.layoutMode() === 'vertical' ? 'horizontal' : 'vertical';
    this.layoutMode.set(next);
    this.storage.setLocal('layoutMode', next);
    this.closeSidebar();
  }

  closeSidebar() {
    this.sidebarOpen.set(false);
  }

  /** "Manage test data" in the TEST MODE bar. */
  goTestMode() {
    this.router.navigate(['/masters/test-mode']);
  }

  toggleUserMenu() {
    this.userMenuOpen.update((v) => !v);
  }

  closeUserMenu() {
    this.userMenuOpen.set(false);
  }

  goChangePassword() {
    this.closeUserMenu();
    this.router.navigate(['/change-password']);
  }

  logout() {
    this.closeUserMenu();
    this.loading.set(true);

    this.auth.logout().subscribe({
      next: (res: any) => {
        this.toast.success(res?.message || 'Logged out successfully');
        this.finishLogout();
      },
      error: () => this.finishLogout(),
    });
  }

  private finishLogout() {
    this.loading.set(false);
    this.storage.clearAll();
    this.menuService.reset(); // drop cached menu so the next user loads their own
    this.kycService.reset(); // drop cached KYC status so the next user loads their own
    this.notificationService.reset(); // drop cached unread count
    this.router.navigate(['/login']);
  }
}
